import { useRef, useState } from 'react';
import { FileWithStatus, FileUploadStatus } from '@/types/upload';
import { useAssetUpload } from '@/hooks/useAssetUpload';
import { getFilename } from '@/utils/path';
import { useQueryClient } from '@tanstack/react-query';
import { GET_ASSET_DEPENDENCIES_QUERY_KEY } from '@/hooks/api/assets/useGetAssetDependencies';

export const useDependencyUpload = () => {
  const pendingDependencyPath = useRef<string>('');
  const [uploadingPaths, setUploadingPaths] = useState<Set<string>>(new Set());
  const queryClient = useQueryClient();

  const uploadContext = useAssetUpload({
    onSuccess: () => {
      void queryClient.resetQueries({
        queryKey: [GET_ASSET_DEPENDENCIES_QUERY_KEY],
      });
      setUploadingPaths(new Set());
    },
  });

  const handleUploadClick = (dependencyPath: string) => {
    pendingDependencyPath.current = dependencyPath;
    uploadContext.fileInputReference.current?.click();
  };

  const createFileInputProperties = (): React.InputHTMLAttributes<HTMLInputElement> => ({
    ...uploadContext.fileInputProps,
    multiple: false,
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
      const dependencyPath = pendingDependencyPath.current;

      if (dependencyPath && event.target.files && event.target.files.length > 0) {
        const file = event.target.files[0];
        const expectedFilename = getFilename(dependencyPath);

        if (file.name.toLowerCase() !== expectedFilename.toLowerCase()) {
          const fileWithStatus: FileWithStatus = {
            id: dependencyPath,
            file,
            name: file.name,
            size: file.size,
            path: dependencyPath,
            status: {
              state: FileUploadStatus.FAILED,
              message: 'Selected file name does not match missing dependency.',
              code: FileUploadStatus.FILENAME_MISMATCH,
            },
          };

          void uploadContext.handleFilesAdded([], [fileWithStatus]);
          pendingDependencyPath.current = '';
          event.target.value = '';
          return;
        }

        const fileWithStatus: FileWithStatus = {
          id: dependencyPath,
          file,
          name: file.name,
          size: file.size,
          path: dependencyPath,
          status: {
            state: FileUploadStatus.PENDING,
          },
        };

        setUploadingPaths(previous => new Set(previous).add(dependencyPath));
        void uploadContext.handleFilesAdded([fileWithStatus], []);

        pendingDependencyPath.current = '';
        event.target.value = '';
      } else {
        uploadContext.fileInputProps.onChange?.(event);
      }
    },
  });

  return {
    ...uploadContext,
    uploadingPaths,
    handleUploadClick,
    fileInputProps: createFileInputProperties(),
  };
};
